import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SEO from '../components/common/SEO';
import { getPropertySEO, getCanonicalUrl } from '../utils/seoConstants';

// Функция для получения ID видео из URL
const getVideoId = (url, source = 'youtube') => {
  if (!url || typeof url !== 'string') {
    console.warn('getVideoId: URL is empty or not a string');
    return null;
  }

  try {
    let videoId = null;

    // Проверяем, не является ли URL уже встроенным
    if (source === 'youtube') {
      if (url.includes('youtube.com/embed/')) {
        videoId = url.split('/').pop().split('?')[0];
        return videoId;
      }
      
      // YouTube URL patterns
      const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/i;
      const match = url.match(youtubeRegex);
      if (match && match[1]) {
        videoId = match[1];
      }
    } else if (source === 'vimeo') {
      if (url.includes('player.vimeo.com/video/')) {
        videoId = url.split('/').pop().split('?')[0];
        return videoId;
      }
      
      // Vimeo URL patterns
      const vimeoRegex = /(?:vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:[a-zA-Z0-9_-]+)?)/i;
      const match = url.match(vimeoRegex);
      if (match && match[1]) {
        videoId = match[1];
      }
    } else if (source === 'rutube') {
      // Проверяем, не является ли URL уже встроенным
      if (url.includes('rutube.ru/play/embed/')) {
        videoId = url.split('/').pop().split('?')[0];
        return videoId;
      }
      
      // Для Rutube shorts с длинным ID
      if (url.includes('rutube.ru/shorts/')) {
        const urlParts = url.split('/');
        // Берем часть после 'shorts/'
        for (let i = 0; i < urlParts.length; i++) {
          if (urlParts[i] === 'shorts' && i + 1 < urlParts.length) {
            // Удаляем слэш в конце, если он есть
            videoId = urlParts[i + 1].replace(/\/$/, '');
            console.log(`Получен ID для Rutube shorts: ${videoId}`);
            return videoId;
          }
        }
      }
      
      // Для обычных видео Rutube
      if (url.includes('rutube.ru/video/')) {
        const urlParts = url.split('/');
        for (let i = 0; i < urlParts.length; i++) {
          if (urlParts[i] === 'video' && i + 1 < urlParts.length) {
            videoId = urlParts[i + 1].replace(/\/$/, '');
            console.log(`Получен ID для Rutube video: ${videoId}`);
            return videoId;
          }
        }
      }
      
      // Если предыдущие методы не сработали, пробуем регулярное выражение
      const rutubeRegex = /(?:rutube\.ru\/(?:video\/|shorts\/|play\/embed\/)([a-zA-Z0-9]+))/i;
      const match = url.match(rutubeRegex);
      if (match && match[1]) {
        videoId = match[1];
        console.log(`Получен ID по регулярке: ${videoId}`);
        return videoId;
      }
      
      // Если все методы не сработали, пробуем извлечь ID из последней части URL
      const urlParts = url.split('/');
      // Берем последнюю часть URL после последнего слэша
      const lastPart = urlParts[urlParts.length - 1];
      if (lastPart && lastPart.length > 0) {
        // Удаляем слэш в конце, если он есть
        videoId = lastPart.replace(/\/$/, '');
        console.log(`Получен ID из последней части URL: ${videoId}`);
      }
    }

    return videoId;
  } catch (error) {
    console.error('Ошибка при получении ID видео:', error);
    return null;
  }
};

// Функция для получения URL для встраивания видео
const getEmbedUrl = (url, source = 'youtube') => {
  // Если URL пустой или не строка
  if (!url || typeof url !== 'string') {
    console.warn('getEmbedUrl: URL пустой или не является строкой:', url);
    return null;
  }
  
  try {
    // Если URL уже является встроенным, вернуть его как есть
    if (url.includes('youtube.com/embed/') || 
        url.includes('player.vimeo.com/video/') || 
        url.includes('rutube.ru/play/embed/')) {
      return url;
    }

    // Прямая обработка Rutube shorts URL
    if (url.includes('rutube.ru/shorts/')) {
      const urlParts = url.split('/');
      for (let i = 0; i < urlParts.length; i++) {
        if (urlParts[i] === 'shorts' && i + 1 < urlParts.length) {
          const videoId = urlParts[i + 1].replace(/\/$/, '');
          console.log(`Получен ID для Rutube shorts напрямую: ${videoId}`);
          return `https://rutube.ru/play/embed/${videoId}`;
        }
      }
    }

    // Получаем ID видео
    const videoId = getVideoId(url, source);
    if (!videoId) {
      console.warn(`getEmbedUrl: Не удалось получить ID видео из URL: ${url}`);
      return null;
    }

    // Формируем URL для встраивания в зависимости от источника
    let embedUrl;
    if (source === 'youtube') {
      embedUrl = `https://www.youtube.com/embed/${videoId}`;
    } else if (source === 'vimeo') {
      embedUrl = `https://player.vimeo.com/video/${videoId}`;
    } else if (source === 'rutube') {
      embedUrl = `https://rutube.ru/play/embed/${videoId}`;
    } else {
      console.warn(`getEmbedUrl: Неизвестный источник видео: ${source}`);
      return null;
    }

    console.log(`Создан URL для встраивания: ${embedUrl}`);
    return embedUrl;
  } catch (error) {
    console.error('Ошибка при получении URL для встраивания видео:', error);
    return null;
  }
};

// Компонент для отображения видео
const VideoCard = ({ video }) => {
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false);

  // Обработчик загрузки превью
  const handleThumbnailLoad = () => {
    setThumbnailLoaded(true);
  };

  // Проверяем наличие URL для видео и превью
  const videoUrl = video.url || '';
  const embedUrl = video.embedUrl || getEmbedUrl(videoUrl, video.source);
  const hasEmbedUrl = embedUrl && embedUrl.trim() !== '';
  const hasThumbnail = video.thumbnailUrl && video.thumbnailUrl.trim() !== '';
  
  // Используем заглушку, если нет превью
  const thumbnailImage = hasThumbnail ? video.thumbnailUrl : '/assets/placeholders/no-video-preview.jpg';
  
  // Отладочная информация
  useEffect(() => {
    if (hasEmbedUrl) {
      console.log(`Загрузка видео "${video.title}" (ID: ${video.id}):`, {
        url: video.url,
        embedUrl: embedUrl,
        source: video.source
      });
    }
  }, [video.id, video.title, video.url, embedUrl, video.source, hasEmbedUrl]);
  
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
        {hasEmbedUrl ? (
          <Box
            component="iframe"
            src={embedUrl}
            title={video.title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 0
            }}
            onError={(e) => console.error('Ошибка загрузки iframe:', e)}
          />
        ) : (
          <>
            <CardMedia
              component="img"
              image={thumbnailImage}
              alt={video.title}
              onLoad={handleThumbnailLoad}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: thumbnailLoaded ? 'block' : 'none'
              }}
            />
            {!thumbnailLoaded && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f0f0f0'
                }}
              >
                <CircularProgress />
              </Box>
            )}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.2)'
              }}
            >
              <Typography variant="body1" color="white">
                Видео недоступно
              </Typography>
            </Box>
          </>
        )}
      </Box>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography gutterBottom variant="h6" component="div">
          {video.title}
        </Typography>
        {video.description && (
          <Typography variant="body2" color="text.secondary">
            {video.description.length > 100
              ? `${video.description.substring(0, 100)}...`
              : video.description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
import StructuredData, { generatePropertySchema, generateBreadcrumbSchema } from '../components/common/StructuredData';
import {
  Box,
  Container,
  Grid,
  Typography,
  CircularProgress,
  Button,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
  Modal,
  Dialog,
  DialogContent,
  Tooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BedIcon from '@mui/icons-material/Bed';
import BathtubIcon from '@mui/icons-material/Bathtub';
import PeopleIcon from '@mui/icons-material/People';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import TelegramIcon from '@mui/icons-material/Telegram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneIcon from '@mui/icons-material/Phone';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import InfoIcon from '@mui/icons-material/Info';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DateRangeIcon from '@mui/icons-material/DateRange';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
// Иконки для кастомных цен, акций и дополнительных услуг
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CelebrationIcon from '@mui/icons-material/Celebration';
import EventIcon from '@mui/icons-material/Event';
import StarIcon from '@mui/icons-material/Star';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import DiscountIcon from '@mui/icons-material/Discount';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SpaIcon from '@mui/icons-material/Spa';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import LocalLaundryServiceIcon from '@mui/icons-material/LocalLaundryService';
import PetsIcon from '@mui/icons-material/Pets';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import PoolIcon from '@mui/icons-material/Pool';
import HotTubIcon from '@mui/icons-material/HotTub';
import FireplaceIcon from '@mui/icons-material/Fireplace';
import OutdoorGrillIcon from '@mui/icons-material/OutdoorGrill';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import WifiIcon from '@mui/icons-material/Wifi';
import TvIcon from '@mui/icons-material/Tv';
import KitchenIcon from '@mui/icons-material/Kitchen';
import MicrowaveIcon from '@mui/icons-material/Microwave';
import CoffeeIcon from '@mui/icons-material/Coffee';
import IronIcon from '@mui/icons-material/Iron';
import WeekendIcon from '@mui/icons-material/Weekend';
import ShowerIcon from '@mui/icons-material/Shower';
import SecurityIcon from '@mui/icons-material/Security';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import SmokingRoomsIcon from '@mui/icons-material/SmokingRooms';
import SmokeFreeIcon from '@mui/icons-material/SmokeFree';
import AccessibleIcon from '@mui/icons-material/Accessible';
import ChildFriendlyIcon from '@mui/icons-material/ChildFriendly';
import NightlifeIcon from '@mui/icons-material/Nightlife';
import SportsBarIcon from '@mui/icons-material/SportsBar';
import CasinoIcon from '@mui/icons-material/Casino';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import SportsFootballIcon from '@mui/icons-material/SportsFootball';
import SportsTennisIcon from '@mui/icons-material/SportsTennis';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import SportsVolleyballIcon from '@mui/icons-material/SportsVolleyball';
import axios from 'axios';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
// Функция для получения компонента иконки по имени
const getIconByName = (iconName) => {
  switch (iconName) {
    // Цены и акции
    case 'LocalOffer':
      return <LocalOfferIcon />;
    case 'Celebration':
      return <CelebrationIcon />;
    case 'Event':
      return <EventIcon />;
    case 'Star':
      return <StarIcon />;
    case 'Favorite':
      return <FavoriteIcon />;
    case 'Gift':
      return <CardGiftcardIcon />;
    case 'Discount':
      return <DiscountIcon />;
    case 'NewReleases':
      return <NewReleasesIcon />;
    case 'EmojiEvents':
      return <EmojiEventsIcon />;
      
    // Удобства и услуги
    case 'Spa':
      return <SpaIcon />;
    case 'LocalBar':
      return <LocalBarIcon />;
    case 'Restaurant':
      return <RestaurantIcon />;
    case 'DirectionsCar':
      return <DirectionsCarIcon />;
    case 'CleaningServices':
      return <CleaningServicesIcon />;
    case 'LocalLaundryService':
      return <LocalLaundryServiceIcon />;
    case 'Pets':
      return <PetsIcon />;
    case 'ChildCare':
      return <ChildCareIcon />;
    case 'FitnessCenter':
      return <FitnessCenterIcon />;
    case 'Pool':
      return <PoolIcon />;
    case 'HotTub':
      return <HotTubIcon />;
    case 'Fireplace':
      return <FireplaceIcon />;
    case 'OutdoorGrill':
      return <OutdoorGrillIcon />;
    case 'BeachAccess':
      return <BeachAccessIcon />;
    case 'AcUnit':
      return <AcUnitIcon />;
    case 'Wifi':
      return <WifiIcon />;
    case 'Tv':
      return <TvIcon />;
    case 'Kitchen':
      return <KitchenIcon />;
    case 'Microwave':
      return <MicrowaveIcon />;
    case 'Coffee':
      return <CoffeeIcon />;
    case 'Iron':
      return <IronIcon />;
    case 'Weekend':
      return <WeekendIcon />;
    case 'Bed':
      return <BedIcon />;
    case 'Shower':
      return <ShowerIcon />;
    case 'Bathtub':
      return <BathtubIcon />;
    case 'Security':
      return <SecurityIcon />;
    case 'LocalParking':
      return <LocalParkingIcon />;
    case 'SmokingRooms':
      return <SmokingRoomsIcon />;
    case 'SmokeFree':
      return <SmokeFreeIcon />;
    case 'Accessible':
      return <AccessibleIcon />;
    case 'ChildFriendly':
      return <ChildFriendlyIcon />;
      
    // Развлечения
    case 'Nightlife':
      return <NightlifeIcon />;
    case 'SportsBar':
      return <SportsBarIcon />;
    case 'Casino':
      return <CasinoIcon />;
    case 'MusicNote':
      return <MusicNoteIcon />;
    case 'SportsEsports':
      return <SportsEsportsIcon />;
    case 'SportsFootball':
      return <SportsFootballIcon />;
    case 'SportsTennis':
      return <SportsTennisIcon />;
    case 'SportsBasketball':
      return <SportsBasketballIcon />;
    case 'SportsVolleyball':
      return <SportsVolleyballIcon />;
      
    default:
      return <LocalOfferIcon />;
  }
};

const PropertyPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [error, setError] = useState(null);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [contactsLoading, setContactsLoading] = useState(true);
  
  // Ref для карты 2GIS
  const mapRef = useRef(null);

  // Fetch property data
  useEffect(() => {
    const fetchPropertyData = async () => {
      try {
        setLoading(true);
        console.log('Fetching property data for ID:', id);
        // Проверяем, является ли ID валидным UUID
        const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
        
        if (!isValidUUID && (id.startsWith('prop-') || id.includes('_id'))) {
          // Если ID не является UUID, но имеет формат prop-xxx или содержит _id
          console.warn('Non-UUID property ID detected:', id, 'Using fallback data.');
          
          // Пробуем получить данные из списка всех свойств
          try {
            const { data: allProperties } = await axios.get('/api/properties');
            if (Array.isArray(allProperties) && allProperties.length > 0) {
              // Используем первое свойство из списка
              const firstProperty = allProperties[0];
              console.log('Using first property from list:', firstProperty.id);
              setProperty(firstProperty);
              setCurrentImageIndex(0);
              return;
            }
          } catch (listError) {
            console.error('Error fetching property list:', listError);
          }
          
          // Если не удалось получить список, используем моковые данные
          const mockProperty = {
            id: id,
            name: 'Демонстрационный объект',
            description: 'Это демонстрационный объект для тестирования интерфейса.',
            address: 'г. Тюмень, ул. Примерная, 123',
            rentalType: 'daily',
            pricePerDay: 5000,
            capacity: 4,
            bedrooms: 2,
            bathrooms: 1,
            area: 80,
            latitude: 57.152985,
            longitude: 65.541227,
            mainImage: '/assets/placeholder-property.jpg',
            PropertyImages: [
              { id: 1, imageUrl: '/assets/placeholder-property.jpg', caption: 'Основное фото' },
              { id: 2, imageUrl: '/assets/placeholder-property.jpg', caption: 'Дополнительное фото' }
            ],
            Amenities: [
              { id: 1, name: 'Wi-Fi', icon: 'wifi' },
              { id: 2, name: 'Парковка', icon: 'local_parking' },
              { id: 3, name: 'Кондиционер', icon: 'ac_unit' }
            ]
          };
          
          setProperty(mockProperty);
          setCurrentImageIndex(0);
        } else {
          // Если ID валидный UUID, делаем запрос к API
          const { data } = await axios.get(`/api/properties/${id}`);
          console.log('Property data received:', data);
          
          // Проверяем наличие связанных видео
          if (data.PropertyVideos && data.PropertyVideos.length > 0) {
            console.log('Found linked videos:', data.PropertyVideos);
          }
          
          setProperty(data);
          setCurrentImageIndex(0);
        }
      } catch (error) {
        console.error('Error fetching property:', error);
        setError('Не удалось загрузить информацию об объекте');
        
        // Используем заглушку при ошибке
        setProperty({
          id: id,
          name: 'Уютный коттедж у озера',
          description: 'Просторный коттедж с видом на озеро, идеально подходит для семейного отдыха.',
          address: 'г. Тюмень, ул. Озерная, 15',
          rentalType: 'daily',
          pricePerDay: 15000,
          capacity: 8,
          bedrooms: 3,
          bathrooms: 2,
          area: 150,
          latitude: 57.1522,
          longitude: 65.5272,
          mainImage: '/assets/placeholder-property.jpg',
          PropertyImages: [
            { id: '1', imageUrl: '/assets/placeholder-property.jpg', caption: 'Вид спереди' },
            { id: '2', imageUrl: '/assets/placeholder-property.jpg', caption: 'Гостиная' }
          ],
          Amenities: [
            { id: '1', name: 'Wi-Fi', icon: 'wifi' },
            { id: '2', name: 'Парковка', icon: 'local_parking' },
            { id: '3', name: 'Сауна', icon: 'hot_tub' }
          ]
        });
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPropertyData();
    } else {
      setError('ID объекта не указан');
      setLoading(false);
    }
  }, [id]);

  // Handle image navigation
  const handleNextImage = () => {
    if (property?.PropertyImages?.length > 0) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === property.PropertyImages.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  const handlePrevImage = () => {
    if (property?.PropertyImages?.length > 0) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === 0 ? property.PropertyImages.length - 1 : prevIndex - 1
      );
    }
  };

  // Initialize 2GIS map
  useEffect(() => {
    // Функция очистки карты
    const cleanupMap = () => {
      try {
        if (mapRef.current) {
          mapRef.current.remove();
          mapRef.current = null;
        }
      } catch (e) {
        console.error('Error cleaning up map:', e);
      }
    };
    
    // Очистка предыдущей карты при повторной инициализации
    cleanupMap();
    
    if (!loading && property && property.latitude && property.longitude) {
      // Проверяем, существует ли контейнер для карты
      const mapContainer = document.getElementById('map-container');
      if (!mapContainer) {
        console.error('Map container not found. Map initialization skipped.');
        return;
      }
      
      const initMap = () => {
        try {
          // Проверяем, что контейнер карты существует
          if (!document.getElementById('map-container')) {
            console.error('Map container not found during initialization');
            return;
          }
          
          // Создаем карту только если она еще не создана
          if (!mapRef.current) {
            console.log('Initializing map with coordinates:', [property.latitude, property.longitude]);
            
            // Проверяем корректность координат
            const coordinates = [parseFloat(property.latitude), parseFloat(property.longitude)];
            if (isNaN(coordinates[0]) || isNaN(coordinates[1])) {
              console.error('Invalid coordinates:', coordinates);
              return;
            }
            
            // Initialize Leaflet map without default attribution control
            mapRef.current = window.L.map('map-container', { attributionControl: false }).setView(coordinates, 15);
            // Add custom attribution control without prefix or emojis
            window.L.control.attribution({ prefix: false }).addTo(mapRef.current);
            window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
              attribution: '© OpenStreetMap contributors'
            }).addTo(mapRef.current);
            
            // Добавляем маркер на карту
            const marker = window.L.marker(coordinates);
            marker.addTo(mapRef.current);
            
            // Добавляем всплывающее окно с информацией и кнопкой для построения маршрута
            const popupContent = `
              <div style="text-align: center;">
                <strong>${property.name}</strong><br>
                ${property.address}<br><br>
                <a href="https://2gis.ru/tyumen/search/${encodeURIComponent(property.address)}" 
                   target="_blank" 
                   style="display: inline-block; background-color: #3498db; color: white; padding: 8px 12px; 
                          text-decoration: none; border-radius: 4px; font-weight: bold;">
                  Открыть в 2ГИС
                </a>
              </div>
            `;
            marker.bindPopup(popupContent, { maxWidth: 300 });
          }
        } catch (e) {
          console.error('Error initializing 2GIS map:', e);
        }
      };
      
      // Ensure Leaflet CSS is loaded
      const ensureLeafletCss = () => {
        if (!document.querySelector('link[href*="leaflet.css"]')) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
          document.head.appendChild(link);
        }
      };

      // Load Leaflet if not already loaded
      if (window.L) {
        ensureLeafletCss();
        initMap();
      } else {
        ensureLeafletCss();
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.async = true;
        script.onload = () => initMap();
        script.onerror = () => {
          console.error('Failed to load Leaflet script');
        };
        document.body.appendChild(script);
      }
    }
    
    // Cleanup function
    return cleanupMap;
  }, [loading, property]);

  // Handle fullscreen image view
  const handleOpenFullscreen = () => {
    setFullscreenOpen(true);
  };

  const handleCloseFullscreen = () => {
    setFullscreenOpen(false);
  };

  // Handle back to gallery
  const handleBackToGallery = () => {
    navigate('/gallery');
  };

  // Contact functions
  const handleContactTelegram = () => {
    window.open('https://t.me/+79630600255', '_blank');
  };

  const handleContactWhatsApp = () => {
    if (phone) {
      window.open(`https://wa.me/${phone}`, '_blank');
    }
  };

  const handleContactPhone = () => {
    if (phone) {
      window.open(`tel:+${phone}`, '_blank');
    }
  };

  // Fetch contact info
  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const response = await axios.get('/api/settings');
        if (response.data && response.data.contactPhone) {
          // Remove all non-digit characters for links
          const cleanPhone = response.data.contactPhone.replace(/\D/g, '');
          setPhone(cleanPhone);
        }
      } catch (error) {
        console.error('Error fetching contact info:', error);
      } finally {
        setContactsLoading(false);
      }
    };

    fetchContactInfo();
  }, []);

  // If loading
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // If error
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h5" color="error" align="center">
          {error}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleBackToGallery}
            startIcon={<ArrowBackIcon />}
          >
            Вернуться в галерею
          </Button>
        </Box>
      </Container>
    );
  }

  // Main render
  return (
    <Box sx={{ py: 4 }}>
      {/* SEO метаданные */}
      {property && (
        <>
          <SEO 
            title={`${property.name} - Аренда в Тюмени | DomikTyumen`}
            description={property.description ? 
              `${property.description.substring(0, 150)}...` : 
              `Аренда ${property.type || 'недвижимости'} в Тюмени. ${property.bedrooms || ''} спальни, ${property.bathrooms || ''} ванные комнаты. Забронируйте сейчас!`}
            keywords={`${property.name}, аренда, ${property.type || 'недвижимость'}, Тюмень, ${property.location || ''}, ${property.bedrooms || ''} спальни`}
            ogImage={property.PropertyImages && property.PropertyImages.length > 0 ? property.PropertyImages[0].imageUrl : '/assets/placeholder-property.jpg'}
            canonicalUrl={getCanonicalUrl(`/property/${property.id}`)}
          />
          
          {/* Структурированные данные для поисковых систем */}
          <StructuredData data={generatePropertySchema(property)} />
          <StructuredData data={generateBreadcrumbSchema([
            { name: 'Главная', path: '/' },
            { name: 'Галерея', path: '/gallery' },
            { name: property.name, path: `/property/${property.id}` }
          ])} />
        </>
      )}
      {/* Модальное окно для просмотра изображений на весь экран */}
      <Dialog
        open={fullscreenOpen}
        onClose={handleCloseFullscreen}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { 
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            boxShadow: 'none',
            height: '100%',
            m: 0,
            borderRadius: 0,
            position: 'relative'
          }
        }}
      >
        <IconButton
          onClick={handleCloseFullscreen}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: '#fff',
            zIndex: 1,
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
          }}
        >
          <CloseIcon />
        </IconButton>
        
        <DialogContent sx={{ p: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          {property?.PropertyImages?.length > 0 && (
            <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img
                src={property.PropertyImages[currentImageIndex].imageUrl}
                alt={property.PropertyImages[currentImageIndex].caption || property.name}
                style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain' }}
              />
              
              {property.PropertyImages.length > 1 && (
                <>
                  <IconButton
                    sx={{
                      position: 'absolute',
                      left: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(0, 0, 0, 0.6)',
                      color: '#fff',
                      '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.8)' },
                      width: 48,
                      height: 48
                    }}
                    onClick={handlePrevImage}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <IconButton
                    sx={{
                      position: 'absolute',
                      right: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(0, 0, 0, 0.6)',
                      color: '#fff',
                      '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.8)' },
                      width: 48,
                      height: 48
                    }}
                    onClick={handleNextImage}
                  >
                    <ArrowForwardIcon />
                  </IconButton>
                  
                  <Typography
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      bgcolor: 'rgba(0, 0, 0, 0.6)',
                      color: 'white',
                      px: 2,
                      py: 0.5,
                      borderRadius: 1
                    }}
                  >
                    {currentImageIndex + 1} / {property.PropertyImages.length}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
      
      <Container maxWidth="lg">
        {/* Back button */}
        <Button
          variant="outlined"
          onClick={handleBackToGallery}
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 3 }}
        >
          Вернуться в галерею
        </Button>

        {/* Property title */}
        <Typography variant="h3" component="h1" gutterBottom>
          {property.name}
        </Typography>

        <Grid container spacing={4}>
          {/* Left column - Images and details */}
          <Grid item xs={12} md={8}>
            {/* Image gallery with fixed height container */}
            <Paper 
              elevation={2} 
              sx={{ 
                position: 'relative', 
                mb: 4, 
                borderRadius: 2, 
                overflow: 'hidden',
                height: 400, // Фиксированная высота
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <Box 
                sx={{ 
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  overflow: 'hidden'
                }}
              >
                <CardMedia
                  component="img"
                  sx={{ 
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover', // Изображение заполняет контейнер в неразвернутом виде
                    transition: 'transform 0.3s ease'
                  }}
                  image={
                    property.PropertyImages && property.PropertyImages.length > 0
                      ? property.PropertyImages[currentImageIndex].imageUrl
                      : property.mainImage || '/assets/placeholder-property.jpg'
                  }
                  alt={property.name}
                />
              </Box>
              
              {/* Кнопка для просмотра на весь экран */}
              <IconButton
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'rgba(0, 0, 0, 0.6)',
                  color: '#fff',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.8)' },
                }}
                onClick={handleOpenFullscreen}
              >
                <FullscreenIcon />
              </IconButton>
              
              {property.PropertyImages && property.PropertyImages.length > 1 && (
                <>
                  <IconButton
                    sx={{
                      position: 'absolute',
                      left: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(0, 0, 0, 0.6)',
                      color: '#fff',
                      '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.8)' },
                      width: 48,
                      height: 48
                    }}
                    onClick={handlePrevImage}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <IconButton
                    sx={{
                      position: 'absolute',
                      right: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(0, 0, 0, 0.6)',
                      color: '#fff',
                      '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.8)' },
                      width: 48,
                      height: 48
                    }}
                    onClick={handleNextImage}
                  >
                    <ArrowForwardIcon />
                  </IconButton>
                  <Typography
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      left: 16,
                      bgcolor: 'rgba(0, 0, 0, 0.6)',
                      color: 'white',
                      px: 2,
                      py: 0.5,
                      borderRadius: 1
                    }}
                  >
                    {currentImageIndex + 1} / {property.PropertyImages.length}
                  </Typography>
                </>
              )}
            </Paper>
            
            {/* Thumbnails row */}
            {property.PropertyImages && property.PropertyImages.length > 1 && (
              <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 1, mb: 4, overflowX: 'auto', pb: 1 }}>
                {property.PropertyImages.map((image, index) => (
                  <Box 
                    key={image.id || index}
                    onClick={() => setCurrentImageIndex(index)}
                    sx={{
                      width: 80,
                      height: 60,
                      flexShrink: 0,
                      borderRadius: 1,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: currentImageIndex === index ? '2px solid #3f51b5' : '2px solid transparent',
                      opacity: currentImageIndex === index ? 1 : 0.7,
                      transition: 'all 0.2s ease',
                      '&:hover': { opacity: 1 }
                    }}
                  >
                    <CardMedia
                      component="img"
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      image={image.imageUrl}
                      alt={image.caption || `Фото ${index + 1}`}
                    />
                  </Box>
                ))}
              </Box>
            )}
            
            {/* Раздел с видео */}
            {property.PropertyVideos && property.PropertyVideos.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 2 }}>
                  Видео ({property.PropertyVideos.length})
                </Typography>
                
                <Grid container spacing={2}>
                  {property.PropertyVideos.map((propertyVideo, index) => {
                    // Получаем данные видео из вложенного объекта Video
                    const video = propertyVideo.Video || {};
                    const videoUrl = video.url || propertyVideo.videoUrl || '';
                    const source = video.source || 'youtube';
                    
                    // Получаем заголовок и описание
                    const title = video.title || propertyVideo.caption || `Видео ${index + 1}`;
                    const description = video.description || propertyVideo.description || '';
                    
                    if (!videoUrl) {
                      console.log(`Видео ${index} не имеет URL`);
                      return null;
                    }
                    
                    console.log(`Видео ${index}:`, { videoUrl, source, title });
                    
                    return (
                      <Grid item xs={12} sm={6} md={4} key={`video-${propertyVideo.id || index}`}>
                        <VideoCard video={{ 
                          id: video.id || propertyVideo.id || index,
                          url: videoUrl,
                          thumbnailUrl: video.thumbnailUrl || propertyVideo.thumbnail,
                          title: title,
                          description: description,
                          source: source
                        }} />
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}

            {/* Property details */}
            <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
              <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {property.type && <Chip label={property.type} />}
                <Chip 
                  label={property.rentalType === 'hourly' ? 'Почасовая аренда' : 'Посуточная аренда'} 
                  color="primary" 
                  variant="outlined" 
                />
              </Box>

              <Typography variant="body1" paragraph sx={{ textAlign: 'justify' }}>
                {property.description}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                {property.capacity && (
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="body2">
                        {property.capacity} человек
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {property.bedrooms && (
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <BedIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="body2">
                        {property.bedrooms} спален
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {property.bathrooms && (
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <BathtubIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="body2">
                        {property.bathrooms} ванных
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {property.area && (
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <SquareFootIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="body2">
                        {property.area} м²
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>

              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                <LocationOnIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body2">
                  {property.address}
                </Typography>
              </Box>
            </Paper>

            {/* Amenities */}
            {property.Amenities && property.Amenities.length > 0 && (
              <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Услуги и удобства
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {property.Amenities.map((amenity) => (
                    <Chip 
                      key={amenity.id} 
                      label={amenity.name} 
                      sx={{ mb: 1 }} 
                    />
                  ))}
                </Box>
              </Paper>
            )}

            {/* 2GIS Map */}
            <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Расположение на карте
              </Typography>
              <Box 
                id="map-container"
                sx={{ 
                  height: '400px', 
                  width: '100%', 
                  position: 'relative', 
                  borderRadius: 2, 
                  overflow: 'hidden'
                }}
              >
                {!property.latitude || !property.longitude ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center', mt: 15 }}>
                    Информация о расположении недоступна
                  </Typography>
                ) : null}
              </Box>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Button 
                  variant="outlined" 
                  color="primary"
                  component="a"
                  href={`https://2gis.ru/tyumen/search/${encodeURIComponent(property.address)}`}
                  target="_blank"
                  sx={{ mt: 1 }}
                >
                  Построить маршрут в 2ГИС
                </Button>
              </Box>
            </Paper>
            

          </Grid>

          {/* Right column - Booking and Contact */}
          <Grid item xs={12} md={4}>
            {/* Price and booking */}
            <Card sx={{ p: 3, position: 'sticky', top: 20, zIndex: 10 }}>
              <Typography variant="h5" gutterBottom>
                Стоимость
              </Typography>
              
              {/* Основные цены */}
              {property.rentalType === 'hourly' ? (
                <Box>
                  {property.hourlyPrices && property.hourlyPrices.length > 0 ? (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Цены за час по количеству гостей:
                      </Typography>
                      <Grid container spacing={1}>
                        {property.hourlyPrices.map((priceItem, index) => (
                          <Grid item xs={6} key={index}>
                            <Typography variant="body2" color="text.secondary">
                              {(priceItem.minGuests !== undefined && priceItem.maxGuests !== undefined && priceItem.minGuests === priceItem.maxGuests) ? 
                                `${priceItem.minGuests} чел.` : 
                                `${priceItem.minGuests !== undefined ? priceItem.minGuests : 1}-${priceItem.maxGuests !== undefined ? priceItem.maxGuests : priceItem.guests} чел.`}: {priceItem.price} ₽/час
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  ) : property.pricePerHour ? (
                    <Typography variant="h6" component="div">
                      {property.pricePerHour} ₽/час
                    </Typography>
                  ) : null}
                  
                  {property.minimumHours > 1 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Минимум {property.minimumHours} ч.
                    </Typography>
                  )}
                  
                  {/* Информация о предоплате для почасовой аренды */}
                  {property.prepayment > 0 && (
                    <Box sx={{ mt: 2, bgcolor: 'primary.light', color: 'primary.contrastText', p: 1, borderRadius: 1 }}>
                      <Typography variant="body2" align="center" sx={{ fontWeight: 'medium' }}>
                        Предоплата: {property.prepayment} ₽
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1, boxShadow: '0 0 5px rgba(0,0,0,0.1)' }}>
                  <Typography variant="h4" color="primary" gutterBottom align="center">
                    от {Math.floor(property.pricePerDay)} ₽/сутки
                  </Typography>
                  
                  {/* Информация о предоплате */}
                  {property.prepayment > 0 && (
                    <Box sx={{ mt: 1, mb: 2, bgcolor: 'primary.light', color: 'primary.contrastText', p: 1, borderRadius: 1 }}>
                      <Typography variant="body2" align="center" sx={{ fontWeight: 'medium' }}>
                        Предоплата: {property.prepayment} ₽
                      </Typography>
                    </Box>
                  )}
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1, justifyContent: 'center' }}>
                    {property.minimumDays && (
                      <Chip 
                        icon={<InfoIcon />} 
                        label={`Минимум: ${property.minimumDays} сут.`} 
                        variant="outlined" 
                        color="primary" 
                        size="small"
                      />
                    )}
                    {property.pricePerWeek && (
                      <Chip 
                        icon={<DateRangeIcon />} 
                        label={`Неделя: ${Math.floor(property.pricePerWeek)} ₽`} 
                        variant="outlined" 
                        color="primary" 
                        size="small"
                      />
                    )}
                    {property.pricePerMonth && (
                      <Chip 
                        icon={<CalendarMonthIcon />} 
                        label={`Месяц: ${Math.floor(property.pricePerMonth)} ₽`} 
                        variant="outlined" 
                        color="primary" 
                        size="small"
                      />
                    )}
                  </Box>
                </Box>
              )}
              
              {/* Дополнительные цены */}
              {property.deposit && (
                <Box sx={{ mt: 2, mb: 1, textAlign: 'center' }}>
                  <Typography variant="body2">
                    <strong>Залог:</strong> {Math.floor(property.deposit)} ₽
                  </Typography>
                </Box>
              )}
              
              {/* Цены по дням недели */}
              {property.rentalType === 'daily' && (
                <Box sx={{ mt: 3, bgcolor: 'background.paper', p: 2, borderRadius: 1, boxShadow: '0 0 5px rgba(0,0,0,0.1)' }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarTodayIcon sx={{ mr: 1, color: 'primary.main' }} />
                    Цены по дням недели
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {/* Будни */}
                      <Box sx={{ flex: 1, minWidth: '45%', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="primary.main" gutterBottom align="center">Будние дни</Typography>
                        {property.mondayPrice && (
                          <Box sx={{ mb: 1, px: 1, textAlign: 'center' }}>
                            <Typography variant="body2">Понедельник</Typography>
                            <Typography variant="body2" fontWeight="bold">{Math.floor(property.mondayPrice)} ₽</Typography>
                          </Box>
                        )}
                        {property.tuesdayPrice && (
                          <Box sx={{ mb: 1, px: 1, textAlign: 'center' }}>
                            <Typography variant="body2">Вторник</Typography>
                            <Typography variant="body2" fontWeight="bold">{Math.floor(property.tuesdayPrice)} ₽</Typography>
                          </Box>
                        )}
                        {property.wednesdayPrice && (
                          <Box sx={{ mb: 1, px: 1, textAlign: 'center' }}>
                            <Typography variant="body2">Среда</Typography>
                            <Typography variant="body2" fontWeight="bold">{Math.floor(property.wednesdayPrice)} ₽</Typography>
                          </Box>
                        )}
                        {property.thursdayPrice && (
                          <Box sx={{ mb: 1, px: 1, textAlign: 'center' }}>
                            <Typography variant="body2">Четверг</Typography>
                            <Typography variant="body2" fontWeight="bold">{Math.floor(property.thursdayPrice)} ₽</Typography>
                          </Box>
                        )}
                      </Box>
                      
                      {/* Выходные */}
                      <Box sx={{ flex: 1, minWidth: '45%', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
                        <Typography variant="subtitle2" color="primary.main" gutterBottom align="center">Выходные дни</Typography>
                        {property.fridayPrice && (
                          <Box sx={{ mb: 1, px: 1, textAlign: 'center' }}>
                            <Typography variant="body2">Пятница</Typography>
                            <Typography variant="body2" fontWeight="bold">{Math.floor(property.fridayPrice)} ₽</Typography>
                          </Box>
                        )}
                        {property.saturdayPrice && (
                          <Box sx={{ mb: 1, px: 1, textAlign: 'center' }}>
                            <Typography variant="body2">Суббота</Typography>
                            <Typography variant="body2" fontWeight="bold">{Math.floor(property.saturdayPrice)} ₽</Typography>
                          </Box>
                        )}
                        {property.sundayPrice && (
                          <Box sx={{ mb: 1, px: 1, textAlign: 'center' }}>
                            <Typography variant="body2">Воскресенье</Typography>
                            <Typography variant="body2" fontWeight="bold">{Math.floor(property.sundayPrice)} ₽</Typography>
                          </Box>
                        )}
                        {property.holidayPrice && (
                          <Box sx={{ mb: 1, px: 1, textAlign: 'center' }}>
                            <Typography variant="body2">Праздники</Typography>
                            <Typography variant="body2" fontWeight="bold">{Math.floor(property.holidayPrice)} ₽</Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
              
              {(property.extraGuestPrice && property.extraGuestPrice > 0) && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                  Доп. гость: +{Math.floor(property.extraGuestPrice)} ₽/чел.
                </Typography>
              )}
              
              {/* Промоакции */}
              {property.promotions && property.promotions.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Специальные предложения:
                  </Typography>
                  {property.promotions.map((promo, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {promo.icon ? (
                        <Box sx={{ mr: 1, color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                          {getIconByName(promo.icon)}
                        </Box>
                      ) : (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                          <LocalOfferIcon color="action" fontSize="small" />
                        </Box>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        <strong>{promo.name}:</strong> {promo.description}
                        {promo.price > 0 && ` (${promo.price} ₽)`}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              
              {/* Дополнительные услуги */}
              {property.additionalServices && property.additionalServices.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Дополнительные услуги:
                  </Typography>
                  {property.additionalServices.map((service, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {service.icon ? (
                        <Box sx={{ mr: 1, color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                          {getIconByName(service.icon)}
                        </Box>
                      ) : (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                          <LocalBarIcon color="action" fontSize="small" />
                        </Box>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        <strong>{service.name || service.description}:</strong> {service.price} ₽
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              
              {/* Кастомные цены */}
              {property.customPrices && property.customPrices.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Специальные тарифы:
                  </Typography>
                  {property.customPrices.map((price, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {price.icon ? (
                        <Box sx={{ mr: 1, color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                          {getIconByName(price.icon)}
                        </Box>
                      ) : (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                          <EventIcon color="action" fontSize="small" />
                        </Box>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        <strong>{price.condition}:</strong> {price.price} ₽
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                sx={{ mt: 3, mb: 1 }}
                onClick={() => navigate(`/booking?property=${property.id}`)}
              >
                Забронировать
              </Button>
              
              {/* Время заезда и выезда - только для посуточных объектов */}
              {property.rentalType === 'daily' && (
                <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Время заезда и выезда
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AccessTimeIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="body1">
                      <strong>Заезд:</strong> {property.checkInTime || '14:00'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AccessTimeIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="body1">
                      <strong>Выезд:</strong> {property.checkOutTime || '12:00'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <InfoIcon sx={{ mr: 1, color: 'primary.main', mt: 0.5, fontSize: '1rem' }} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                      Поздний выезд или ранний заезд возможен по договоренности.
                    </Typography>
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Contact options */}
              <Typography variant="h6" gutterBottom>
                Связаться с нами
              </Typography>
              <List>
                <ListItem disableGutters>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<TelegramIcon />}
                    onClick={handleContactTelegram}
                    sx={{ justifyContent: 'flex-start', mb: 1 }}
                  >
                    Telegram
                  </Button>
                </ListItem>
                <ListItem disableGutters>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<WhatsAppIcon />}
                    onClick={handleContactWhatsApp}
                    sx={{ justifyContent: 'flex-start', mb: 1 }}
                  >
                    WhatsApp
                  </Button>
                </ListItem>
                <ListItem disableGutters>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<PhoneIcon />}
                    onClick={handleContactPhone}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Позвонить
                  </Button>
                </ListItem>
              </List>

              {/* External platform links */}
              {(property.dgisLink || property.yandexLink || property.avitoLink || property.vkLink || property.instagramLink) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Объект на других платформах
                  </Typography>
                  <List>
                    {property.dgisLink && (
                      <ListItem disableGutters>
                        <Button
                          variant="outlined"
                          fullWidth
                          component={Link}
                          href={property.dgisLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ justifyContent: 'flex-start', mb: 1 }}
                          startIcon={
                            <Box 
                              component="img" 
                              src="/assets/resources_logos/2gis.jpg" 
                              alt="2GIS" 
                              sx={{ height: 24, width: 'auto' }} 
                            />
                          }
                        >
                          2ГИС
                        </Button>
                      </ListItem>
                    )}
                    
                    {property.yandexLink && (
                      <ListItem disableGutters>
                        <Button
                          variant="outlined"
                          fullWidth
                          component={Link}
                          href={property.yandexLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ justifyContent: 'flex-start', mb: 1 }}
                          startIcon={
                            <Box 
                              component="img" 
                              src="/assets/resources_logos/yandex.png" 
                              alt="Яндекс" 
                              sx={{ height: 24, width: 'auto' }} 
                            />
                          }
                        >
                          Яндекс.Карты
                        </Button>
                      </ListItem>
                    )}
                    
                    {property.avitoLink && (
                      <ListItem disableGutters>
                        <Button
                          variant="outlined"
                          fullWidth
                          component={Link}
                          href={property.avitoLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ justifyContent: 'flex-start', mb: 1 }}
                          startIcon={
                            <Box 
                              component="img" 
                              src="/assets/resources_logos/avito.png" 
                              alt="Авито" 
                              sx={{ height: 24, width: 'auto' }} 
                            />
                          }
                        >
                          Авито
                        </Button>
                      </ListItem>
                    )}
                    
                    {property.vkLink && (
                      <ListItem disableGutters>
                        <Button
                          variant="outlined"
                          fullWidth
                          component={Link}
                          href={property.vkLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ justifyContent: 'flex-start', mb: 1 }}
                          startIcon={
                            <Box 
                              component="img" 
                              src="/assets/resources_logos/vk.png" 
                              alt="ВКонтакте" 
                              sx={{ height: 24, width: 'auto' }} 
                            />
                          }
                        >
                          ВКонтакте
                        </Button>
                      </ListItem>
                    )}
                    
                    {property.instagramLink && (
                      <ListItem disableGutters>
                        <Button
                          variant="outlined"
                          fullWidth
                          component={Link}
                          href={property.instagramLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ justifyContent: 'flex-start' }}
                          startIcon={
                            <Box 
                              component="img" 
                              src="/assets/resources_logos/insta.png" 
                              alt="Instagram" 
                              sx={{ height: 24, width: 'auto' }} 
                            />
                          }
                        >
                          Instagram
                        </Button>
                      </ListItem>
                    )}
                  </List>
                </>
              )}
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default PropertyPage;
